import React, { useRef, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Mic, Square, Trash2 } from "lucide-react";

/**
 * Compact voice-note recorder + player.
 * Uses MediaRecorder → uploads to `/api/upload/voice` → gives caller the URL
 * and an optional Whisper transcript so text stays searchable.
 *
 * Props:
 *   - url:              existing voice-note URL (or empty for "not recorded yet")
 *   - transcript:       optional pre-existing transcript to render
 *   - onChange(url, transcript?):  called with new URL after upload, or "" after clear
 *   - testIdPrefix:     data-testid prefix for the two buttons + player
 */
export default function VoiceNoteCapture({ url, transcript, onChange, testIdPrefix }) {
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Microphone not available on this device");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        chunksRef.current = [];
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        if (!blob.size) return;
        setUploading(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "note.webm");
          const { data } = await api.post("/upload/voice", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          onChange(data.url, data.transcript || "");
          if (data.transcript) toast.success("Voice note saved & transcribed");
          else toast.success("Voice note saved");
        } catch (err) {
          toast.error("Voice upload failed");
        } finally {
          setUploading(false);
        }
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch (err) {
      toast.error("Microphone permission denied");
    }
  };

  const stop = () => {
    try { recorderRef.current?.stop(); } catch (e) { /* noop */ }
    setRecording(false);
  };

  return (
    <div className="mt-1.5 space-y-2" data-testid={testIdPrefix}>
      <div className="flex items-center gap-3">
        {!recording && !url && (
          <button
            type="button"
            onClick={start}
            disabled={uploading}
            className="inline-flex items-center gap-2 rounded-full border border-[#E4DFCF] hover:border-[#C9A24B] px-4 py-1.5 text-sm text-[#0F1E15]"
            data-testid={`${testIdPrefix}-record`}
          >
            <Mic size={14} /> {uploading ? "Uploading…" : "Record voice note"}
          </button>
        )}
        {recording && (
          <button
            type="button"
            onClick={stop}
            className="inline-flex items-center gap-2 rounded-full bg-red-600 hover:bg-red-700 text-white px-4 py-1.5 text-sm animate-pulse"
            data-testid={`${testIdPrefix}-stop`}
          >
            <Square size={12} /> Stop
          </button>
        )}
        {url && !recording && (
          <>
            <audio
              src={fileUrl(url)}
              controls
              className="h-8"
              data-testid={`${testIdPrefix}-player`}
            />
            <button
              type="button"
              onClick={() => onChange("", "")}
              className="text-[10px] uppercase tracking-widest text-[#4A5A50] hover:text-red-600 inline-flex items-center gap-1"
              data-testid={`${testIdPrefix}-clear`}
            >
              <Trash2 size={12} /> Clear
            </button>
          </>
        )}
      </div>
      {transcript ? (
        <div
          className="rounded-lg border border-[#E4DFCF] bg-[#FAF7EF] p-2.5 text-sm text-[#0F1E15]"
          data-testid={`${testIdPrefix}-transcript`}
        >
          <span className="text-[10px] uppercase tracking-widest text-[#4A5A50] mr-2">
            Transcript
          </span>
          {transcript}
        </div>
      ) : null}
    </div>
  );
}
